import { mediaService } from '@/api/media.service';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import type {
  CreateMediaDto,
  UpdateMediaDto,
  MediaControllerFindAllData,
} from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';
import type { MutationDefaultsContext } from './query-client';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';

const MEDIA_QUERY_KEY = 'media';

export const useMediaList = (
  query: MaybeRefOrGetter<MediaControllerFindAllData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [MEDIA_QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      mediaService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
    enabled: toRef(enabled),
  });
};

export const useMedia = (id: MaybeRefOrGetter<string | undefined | null>) => {
  return useQuery<RosMedia>({
    queryKey: [MEDIA_QUERY_KEY, id],
    queryFn: () => mediaService.getOne(toValue(id)!),
    enabled: toRef(() => !!toValue(id)),
  });
};

export const useCreateMedia = () => {
  const queryClient = useQueryClient();
  return useMutation<RosMedia, Error, CreateMediaDto>({
    mutationFn: (data) => mediaService.create(data),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Media created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<RosMedia>([MEDIA_QUERY_KEY], result, queryClient);
    },
  });
};

export const useUpdateMedia = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMediaDto }) =>
      mediaService.update(id, data),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Media updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<RosMedia>([MEDIA_QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mediaService.remove(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Media deleted',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<RosMedia>([MEDIA_QUERY_KEY], variables, queryClient);
    },
  });
};

export const useUploadMediaFile = () => {
  return useMutation({
    mutationFn: ({ file, originalFileName }: { file: Blob; originalFileName: string }) =>
      mediaService.uploadVideoFile(originalFileName, file),
    onMutate: () => ({ showSuccessToast: false }),
  });
};

export { MEDIA_QUERY_KEY };
